import { useState } from 'react';
import { motion } from 'framer-motion';
import {
  ChevronLeft, Clock, MapPin, Tag, Globe, Loader2, CheckCircle2,
  Mail, Phone, MessageCircle, Sparkles,
  Calendar, Wrench, Rocket, GraduationCap, Target, Headphones,
} from 'lucide-react';
import { apiCall } from './api';

const FORMATION_NAME = "Création de sites web et d'applications avec l'IA";
const CONTACT_EMAIL = 'support.josmarttech@gmail.com';
const WHATSAPP_NUMBER = '14313345083';
const WHATSAPP_MSG =
  "Bonjour JoSmartTech, je viens de faire mon enregistrement pour la formation accélérée « Création de sites web et d'applications avec l'IA ». " +
  "J'aimerais connaître les dates de début disponibles et discuter de la flexibilité (présentiel ou en ligne) pour commencer.";
const WHATSAPP_URL = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(WHATSAPP_MSG)}`;

const PROGRAM = [
  { t: 'Notions de base', d: 'Comprendre le web, les applications et le rôle de l’IA dans la création moderne.' },
  { t: 'Étude de marché & idée gagnante', d: 'Analyser le marché, cibler un vrai besoin et valider une idée rentable.' },
  { t: 'Architecture du projet', d: 'Définir la structure, les écrans et le parcours utilisateur (site ou application) avant de développer.' },
  { t: 'Les bons outils de développement', d: 'Choisir les outils et plateformes adaptés à ton projet.' },
  { t: 'Développer avec l’IA comme assistant', d: 'Construire tes sites et applications, l’IA t’aidant à coder plus vite.' },
  { t: 'Faire travailler plusieurs IA ensemble', d: 'Combiner plusieurs intelligences artificielles pour aller plus vite et plus loin.' },
  { t: 'Back-end & front-end', d: 'Comprendre les bases du code et savoir modifier ton projet toi-même.' },
  { t: 'Acheter un nom de domaine', d: 'Choisir et acquérir un nom de domaine professionnel.' },
  { t: 'Choisir un hébergement', d: 'Trouver et configurer un hébergement fiable.' },
  { t: 'Publier & déployer', d: 'Mettre ton site en ligne ou déployer ton application pour la rendre accessible à tous.' },
  { t: 'Maintenance & évolution', d: 'Mettre à jour, améliorer et faire évoluer ton projet dans le temps.' },
];

export default function WebAiFormationPage({ onBack }) {
  const [form, setForm] = useState({ name: '', phone: '', email: '', message: '' });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  const submit = async () => {
    if (!form.name.trim()) { setError('Merci d’indiquer ton nom.'); return; }
    setError(''); setLoading(true);
    try {
      await apiCall('/enroll', 'POST', { ...form, formation: FORMATION_NAME });
      setDone(true);
    } catch {
      setError("Une erreur est survenue. Réessaie, s’il te plaît.");
    } finally { setLoading(false); }
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="max-w-3xl mx-auto px-4 py-10" data-testid="webai-formation-page">
      <button onClick={onBack} className="text-white/60 hover:text-white text-sm mb-6 flex items-center gap-1">
        <ChevronLeft size={16} />Retour aux formations
      </button>

      {/* Hero */}
      <div className="rounded-3xl p-8 bg-gradient-to-br from-blue-800 to-purple-900 border border-blue-400/20 relative overflow-hidden">
        <div className="absolute -right-6 -bottom-8 text-9xl opacity-10">🌐</div>
        <div className="inline-flex items-center gap-2 bg-white/10 border border-white/15 rounded-full px-3 py-1 text-xs text-blue-100 mb-3">
          <Globe size={12} />Formation accélérée · Web & applications
        </div>
        <h1 className="text-3xl sm:text-4xl font-black leading-tight">{FORMATION_NAME}</h1>
        <p className="text-white/80 mt-3 text-sm sm:text-base max-w-xl">
          Une formation <strong>accélérée</strong> : de l'idée jusqu'à la mise en ligne, apprends à concevoir,
          développer et publier des <strong>sites web</strong> et des <strong>applications</strong> professionnels.
          L'IA t'accompagne comme assistant pour aller plus vite.
        </p>
        <div className="flex flex-wrap gap-2 mt-5">
          <span className="inline-flex items-center gap-1.5 text-xs font-semibold bg-white/15 px-3 py-1.5 rounded-full"><Clock size={12} />10 heures</span>
          <span className="inline-flex items-center gap-1.5 text-xs font-semibold bg-white/15 px-3 py-1.5 rounded-full">Adaptable selon tes besoins</span>
          <span className="inline-flex items-center gap-1.5 text-xs font-semibold bg-white/15 px-3 py-1.5 rounded-full"><MapPin size={12} />Présentiel ou en ligne</span>
          <span className="inline-flex items-center gap-1.5 text-xs font-semibold bg-yellow-400 text-slate-900 px-3 py-1.5 rounded-full"><Tag size={12} />Sur demande</span>
        </div>
      </div>

      {/* Déroulement */}
      <div className="mt-6 rounded-2xl bg-white/5 border border-white/10 p-5" data-testid="webai-howto">
        <h2 className="font-bold text-lg flex items-center gap-2"><Sparkles size={16} className="text-yellow-300" />Comment se déroule la formation</h2>
        <p className="text-sm text-white/75 mt-2 leading-relaxed">
          Une formation <strong>accélérée et 100% pratique</strong> : plutôt que de longs cours théoriques, tu passes vite à
          l'action et tu construis un vrai projet pas à pas, accompagné par le formateur. Chaque étape du programme ci-dessous
          est expliquée puis mise en pratique immédiatement, jusqu'à la mise en ligne.
        </p>

        <div className="grid sm:grid-cols-2 gap-3 mt-4">
          <InfoItem icon={MapPin} title="Format au choix">
            En <strong>présentiel</strong> avec le formateur, ou <strong>en ligne</strong> à distance — tu décides selon ce qui t'arrange.
          </InfoItem>
          <InfoItem icon={Clock} title="Durée : 10 heures">
            <strong>Adaptable selon tes besoins</strong> et ton rythme de compréhension.
          </InfoItem>
          <InfoItem icon={Calendar} title="Rythme flexible">
            Réparti sur <strong>plusieurs jours</strong>, planifié selon ta disponibilité et ton emploi du temps.
          </InfoItem>
          <InfoItem icon={Wrench} title="Apprentissage par la pratique">
            Tu travailles sur des <strong>cas réels</strong> et tu construis en direct, pas seulement en théorie.
          </InfoItem>
          <InfoItem icon={Rocket} title="Un projet concret">
            Tu repars avec un <strong>site ou une application</strong> réellement publié(e) et fonctionnel(le).
          </InfoItem>
          <InfoItem icon={GraduationCap} title="Accessible à tous">
            <strong>Aucun prérequis</strong> en programmation. Un ordinateur et de la motivation suffisent.
          </InfoItem>
          <InfoItem icon={Headphones} title="Accompagnement">
            Un <strong>suivi personnalisé</strong> et la possibilité de poser tes questions sur WhatsApp.
          </InfoItem>
          <InfoItem icon={Target} title="À la fin, tu sauras">
            <strong>Concevoir, développer, publier et faire évoluer</strong> ton projet en t'appuyant sur l'IA.
          </InfoItem>
        </div>
      </div>

      {/* Programme */}
      <div className="mt-6">
        <h2 className="font-bold text-lg mb-4">Programme de la formation</h2>
        <ol className="space-y-3" data-testid="webai-program">
          {PROGRAM.map((s, i) => (
            <li key={i} className="flex gap-3 rounded-xl bg-white/5 border border-white/10 p-4">
              <div className="shrink-0 w-7 h-7 rounded-full bg-blue-500/25 border border-blue-400/30 flex items-center justify-center text-sm font-bold text-blue-100">{i + 1}</div>
              <div>
                <div className="font-semibold text-white">{s.t}</div>
                <div className="text-sm text-white/65 mt-0.5">{s.d}</div>
              </div>
            </li>
          ))}
        </ol>
      </div>

      {/* Inscription */}
      <div className="mt-8 rounded-2xl bg-gradient-to-br from-blue-900/40 to-slate-900 border border-blue-400/20 p-6" data-testid="enroll-section">
        <h2 className="font-bold text-xl">S'inscrire à la formation</h2>
        <p className="text-sm text-white/70 mt-1">Remplis ce formulaire. Nous te recontacterons pour organiser le début des cours.</p>

        {!done ? (
          <div className="mt-4 space-y-3">
            <Field label="Ton nom complet *" value={form.name} onChange={v => setForm({ ...form, name: v })} placeholder="Ex : Jean Dupont" testid="enroll-name" />
            <div className="grid sm:grid-cols-2 gap-3">
              <Field label="Téléphone / WhatsApp" value={form.phone} onChange={v => setForm({ ...form, phone: v })} placeholder="+1 ..." testid="enroll-phone" />
              <Field label="Adresse e-mail" value={form.email} onChange={v => setForm({ ...form, email: v })} placeholder="ton@email.com" testid="enroll-email" />
            </div>
            <div>
              <label className="text-xs text-white/60">Un message (optionnel)</label>
              <textarea value={form.message} onChange={e => setForm({ ...form, message: e.target.value })} rows={2}
                className="w-full mt-1 bg-slate-800 border border-white/10 rounded-lg p-2.5 text-sm text-white placeholder-white/30 focus:border-blue-400/50 outline-none"
                placeholder="Ex : Je préfère le format en ligne le week-end." data-testid="enroll-message" />
            </div>
            {error && <div className="text-rose-300 text-sm">{error}</div>}
            <button onClick={submit} disabled={loading} data-testid="enroll-submit"
              className="w-full bg-blue-500 hover:bg-blue-400 disabled:opacity-60 text-white font-bold py-3 rounded-xl flex items-center justify-center gap-2 transition">
              {loading ? <Loader2 size={18} className="animate-spin" /> : <CheckCircle2 size={16} />}
              {loading ? 'Envoi...' : 'Envoyer mon inscription'}
            </button>
          </div>
        ) : (
          <div className="mt-4 text-center py-3 space-y-3" data-testid="enroll-success">
            <div className="w-14 h-14 rounded-full bg-emerald-500/20 border border-emerald-400/30 flex items-center justify-center mx-auto">
              <CheckCircle2 size={28} className="text-emerald-300" />
            </div>
            <h4 className="text-white font-bold text-lg">Merci {form.name.split(' ')[0]} ! 🎉</h4>
            <p className="text-white/75 text-sm">Ton inscription a bien été enregistrée.</p>
          </div>
        )}

        {/* WhatsApp CTA — toujours visible */}
        <div className="mt-5 rounded-xl border border-emerald-400/25 bg-emerald-500/[0.07] p-4">
          <p className="text-sm text-white/85 leading-relaxed">
            Pour voir les <strong>créneaux disponibles</strong> ou discuter de ta <strong>flexibilité</strong> (dates, présentiel ou en ligne),
            discute directement avec nous sur WhatsApp 👇
          </p>
          <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" data-testid="enroll-whatsapp"
            className="mt-3 w-full inline-flex items-center justify-center gap-2 bg-emerald-500 hover:bg-emerald-400 text-white font-bold py-3 rounded-xl transition">
            <MessageCircle size={16} />Discuter sur WhatsApp
          </a>
          <div className="mt-3 flex flex-col sm:flex-row gap-2 text-xs text-white/60">
            <span className="inline-flex items-center gap-1.5"><Phone size={12} />+{WHATSAPP_NUMBER}</span>
            <a href={`mailto:${CONTACT_EMAIL}`} className="inline-flex items-center gap-1.5 hover:text-white" data-testid="enroll-email-link"><Mail size={12} />{CONTACT_EMAIL}</a>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

function Field({ label, value, onChange, placeholder, testid }) {
  return (
    <div>
      <label className="text-xs text-white/60">{label}</label>
      <input value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder}
        className="w-full mt-1 bg-slate-800 border border-white/10 rounded-lg p-2.5 text-sm text-white placeholder-white/30 focus:border-blue-400/50 outline-none"
        data-testid={testid} />
    </div>
  );
}

function InfoItem({ icon: Icon, title, children }) {
  return (
    <div className="flex gap-3 rounded-xl bg-white/[0.04] border border-white/10 p-3.5">
      <div className="shrink-0 w-8 h-8 rounded-lg bg-blue-500/20 border border-blue-400/25 flex items-center justify-center">
        <Icon size={15} className="text-blue-200" />
      </div>
      <div>
        <div className="font-semibold text-white text-sm">{title}</div>
        <div className="text-xs text-white/65 mt-0.5 leading-relaxed">{children}</div>
      </div>
    </div>
  );
}